import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ArrowLeft,
  Download,
  FileImage,
  Loader2,
  Sparkles,
  BookOpen,
  ChevronDown,
  ChevronUp,
  LayoutDashboard,
} from 'lucide-react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { Button } from '../components/core/Button';
import { Badge } from '../components/core/Badge';
import { A4Paper } from '../components/pdf/A4Paper';
import type { GeneratedExamPaper } from '../types/generated';
import { useDashboardData } from '../hooks/useDashboardData';
import { useTranslation } from '../context/LanguageContext';

interface LocationState {
  examPaper: GeneratedExamPaper;
}

// ─── Shared viewer body (used by both full-page and embedded) ─────────────────

function ExamViewerBody({
  examPaper,
  onBack,
  backLabel,
}: Readonly<{ examPaper: GeneratedExamPaper; onBack?: () => void; backLabel?: string }>) {
  const { t } = useTranslation();
  const paperRef = useRef<HTMLDivElement>(null);
  const [downloading, setDownloading] = useState<'pdf' | 'png' | null>(null);
  const [showAnswerKey, setShowAnswerKey] = useState(false);

  async function captureCanvas(): Promise<HTMLCanvasElement> {
    if (!paperRef.current) throw new Error('Paper ref not found');
    return html2canvas(paperRef.current, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#ffffff',
      logging: false,
    });
  }

  async function handleDownloadPDF() {
    setDownloading('pdf');
    try {
      const canvas = await captureCanvas();
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageWidth = 210;
      const pageHeight = 297;
      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * pageWidth) / canvas.width;
      let yOffset = 0;
      let remainingHeight = imgHeight;
      while (remainingHeight > 0) {
        if (yOffset > 0) pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, -yOffset, imgWidth, imgHeight);
        yOffset += pageHeight;
        remainingHeight -= pageHeight;
      }
      pdf.save(`${examPaper.subject ?? 'exam'}-questions.pdf`);
    } finally {
      setDownloading(null);
    }
  }

  async function handleDownloadPNG() {
    setDownloading('png');
    try {
      const canvas = await captureCanvas();
      const link = document.createElement('a');
      link.download = `${examPaper.subject ?? 'exam'}-questions.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } finally {
      setDownloading(null);
    }
  }

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="flex items-center gap-1.5 text-xs font-medium text-slate-400 hover:text-violet-400 transition-colors flex-shrink-0"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              {backLabel ?? t('common.back')}
            </button>
          )}
          <div className="min-w-0">
            <p className="text-sm font-bold text-white truncate">
              {examPaper.subject} — {examPaper.paper}
            </p>
            <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
              <Badge variant="default">
                {t('questions.count', { count: examPaper.totalQuestions })}
              </Badge>
              <Badge variant="secondary">
                {examPaper.examType === 'written' ? t('common.written') : t('common.mcq')}
              </Badge>
              <Badge variant="ai">{t('questions.aiGenerated')}</Badge>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button
            size="sm"
            variant="outline"
            onClick={handleDownloadPNG}
            disabled={downloading !== null}
          >
            {downloading === 'png' ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <FileImage className="h-4 w-4" />
            )}
            {t('questions.png')}
          </Button>
          <Button size="sm" onClick={handleDownloadPDF} disabled={downloading !== null}>
            {downloading === 'pdf' ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            {t('questions.downloadPdf')}
          </Button>
        </div>
      </div>

      {/* Info banner */}
      <div className="bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border border-emerald-500/30 rounded-2xl px-5 py-3.5 flex items-center gap-3 shadow-sm">
        <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-emerald-500/20 flex-shrink-0">
          <Sparkles className="h-4 w-4 text-emerald-400" />
        </div>
        <p className="text-sm text-emerald-300">
          {t('questions.banner', {
            count: examPaper.totalQuestions,
            chapters: examPaper.chapters.join(', '),
          })}
        </p>
      </div>

      {/* Paper */}
      <div className="flex flex-col items-center gap-4">
        <div
          className="shadow-2xl rounded overflow-hidden ring-1 ring-black/5"
          style={{ width: '794px', maxWidth: '100%' }}
        >
          <A4Paper ref={paperRef} paper={examPaper} />
        </div>

        {/* Answer Key */}
        <div className="w-full" style={{ width: '794px', maxWidth: '100%' }}>
          <button
            onClick={() => setShowAnswerKey((v) => !v)}
            className="w-full flex items-center justify-between gap-2 rounded-2xl border border-slate-700 bg-slate-800 px-5 py-3.5 text-sm font-semibold text-slate-300 shadow-sm hover:bg-slate-700 transition-all duration-200"
          >
            <span className="flex items-center gap-2">
              <div className="flex h-6 w-6 items-center justify-center rounded-lg bg-violet-500/20">
                <BookOpen className="h-3.5 w-3.5 text-violet-400" />
              </div>
              {t('questions.answerKey')}
            </span>
            {showAnswerKey ? (
              <ChevronUp className="h-4 w-4 text-slate-400" />
            ) : (
              <ChevronDown className="h-4 w-4 text-slate-400" />
            )}
          </button>
          {showAnswerKey && (
            <div className="mt-2 rounded-2xl border border-slate-700 bg-slate-800 shadow-sm divide-y divide-slate-700 overflow-hidden animate-slide-up">
              {examPaper.questions.map((q) => (
                <div key={q.number} className="px-5 py-4 space-y-3">
                  <p className="text-sm font-bold text-white">
                    {t('questions.questionNumber', { number: q.number })}
                    <span className="ml-2 text-xs font-medium text-slate-500">[{q.chapter}]</span>
                  </p>
                  {q.parts.map((part) => (
                    <div key={part.label} className="flex gap-3 text-sm">
                      <span className="inline-flex h-6 w-6 items-center justify-center rounded-lg bg-gradient-to-br from-brand-500 to-violet-600 text-white text-xs font-bold flex-shrink-0 mt-0.5">
                        {part.label}
                      </span>
                      <div className="flex-1 space-y-1">
                        <p className="text-slate-500 text-xs">{part.question}</p>
                        <p className="text-slate-200 font-medium whitespace-pre-wrap">
                          {part.answer}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Inline embed (used inside dashboard overview) ────────────────────────────

export function GeneratedQuestionsEmbed({
  examPaper,
  onClose,
}: Readonly<{ examPaper: GeneratedExamPaper; onClose: () => void }>) {
  const { t } = useTranslation();

  return (
    <ExamViewerBody
      examPaper={examPaper}
      onBack={onClose}
      backLabel={t('wizard.backToOverview')}
    />
  );
}

// ─── Full-page route component (/questions) ───────────────────────────────────

export function GeneratedQuestionsPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const [downloading, setDownloading] = useState<'pdf' | 'png' | null>(null);
  const { addEntry } = useDashboardData();

  const examPaper = (location.state as LocationState | null)?.examPaper ?? null;

  // Save to history once on mount
  useEffect(() => {
    if (examPaper) {
      addEntry(examPaper);
    }
  }, [examPaper, addEntry]);

  if (!examPaper) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-slate-900">
        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-500 to-violet-600">
          <Sparkles className="h-8 w-8 text-white" />
        </div>
        <p className="text-slate-400 text-sm">{t('questions.noPaper')}</p>
        <Link to="/">
          <Button variant="outline">
            <ArrowLeft className="h-4 w-4" />
            {t('payment.backToDashboard')}
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      <header className="sticky top-0 z-20 bg-slate-800/90 backdrop-blur-xl border-b border-slate-700 shadow-sm">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <Link to="/">
              <Button variant="outline" size="sm">
                <ArrowLeft className="h-4 w-4" />
                {t('common.back')}
              </Button>
            </Link>
            <div className="min-w-0">
              <h1 className="text-sm font-bold text-white truncate">
                {examPaper.subject} — {examPaper.paper}
              </h1>
              <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                <Badge variant="default">
                  {t('questions.count', { count: examPaper.totalQuestions })}
                </Badge>
                <Badge variant="secondary">
                  {examPaper.examType === 'written' ? t('common.written') : t('common.mcq')}
                </Badge>
                <Badge variant="ai">{t('questions.aiGenerated')}</Badge>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Link to="/">
              <Button size="sm" variant="outline">
                <LayoutDashboard className="h-4 w-4" />
                {t('common.dashboard')}
              </Button>
            </Link>
            <Button
              size="sm"
              variant="outline"
              disabled={downloading !== null}
              onClick={async () => {
                setDownloading('png');
              }}
            >
              <FileImage className="h-4 w-4" />
              {t('questions.png')}
            </Button>
            <Button size="sm" disabled={downloading !== null}>
              <Download className="h-4 w-4" />
              {t('questions.downloadPdf')}
            </Button>
          </div>
        </div>
      </header>
      <main className="py-8 px-4 flex justify-center">
        <div className="w-full max-w-5xl">
          <ExamViewerBody examPaper={examPaper} />
        </div>
      </main>
    </div>
  );
}
